import { ReactNode, useState } from "react"
import { Head, usePage, router } from "@inertiajs/react"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table"
import { Mail, Users, UserCheck, UserX, Trash2, Power } from "lucide-react"
import AdminLayout from "../../layouts/AdminLayout"
import Preloader from "../../components/ui/preloader"
import DataTableResults from "../../components/ui/data-table-results"
import DataTablePagination from "../../components/ui/data-table-pagination"

interface NewsletterSubscriber {
  id: number
  email: string
  active: boolean
  subscribed_at: string
  unsubscribed_at: string | null
}

interface PaginationData {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

declare module '@inertiajs/core' {
  interface PageProps {
    subscribers: NewsletterSubscriber[],
    stats: {
      total: number
      active: number
      inactive: number
    },
    subscribers_pagination: PaginationData
  }
}

const NewsletterSubscribersIndex = () => {
  const { subscribers, stats, subscribers_pagination } = usePage().props
  const [isLoading, setIsLoading] = useState(false)

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this subscriber?')) {
      router.delete(`/admin/newsletter_subscribers/${id}`)
    }
  }

  const handleToggleStatus = (id: number, currentStatus: boolean) => {
    const action = currentStatus ? 'deactivate' : 'activate'
    if (confirm(`Are you sure you want to ${action} this subscriber?`)) {
      router.patch(`/admin/newsletter_subscribers/${id}/toggle_status`, {}, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          // Optionally show a success message or update local state
          // console.log(`Subscriber ${action}d successfully`)
        }
      })
    }
  }

  const getStatusBadge = (active: boolean) => {
    return active ? (
      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-xs">Active</span>
    ) : (
      <span className="bg-red-100 text-red-800 px-2 py-1 rounded-md text-xs">Inactive</span>
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handlePageChange = (page: number) => {
    setIsLoading(true)
    router.get('/admin/newsletter_subscribers', { page: page }, {
      preserveState: true,
      preserveScroll: true,
      onFinish: () => setIsLoading(false)
    })
  }

  return (
    <>
      <Head title="Newsletter Subscribers" />
      
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold">Newsletter Subscribers</h1>
          <p className="text-muted-foreground">Manage your newsletter subscription list</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Subscribers</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Subscribers</CardTitle>
              <UserCheck className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Inactive Subscribers</CardTitle>
              <UserX className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.inactive}</div>
            </CardContent>
          </Card>
        </div>

        {/* Subscribers Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Subscribers List
            </CardTitle>
          </CardHeader>
          <CardContent>
            {subscribers_pagination && <DataTableResults pagination={subscribers_pagination} object_name="subscribers" />}

            <Preloader isLoading={isLoading}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Subscribed</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscribers.map((subscriber) => (
                    <TableRow key={subscriber.id}>
                      <TableCell>{subscriber.email}</TableCell>
                      <TableCell>
                        {getStatusBadge(subscriber.active)}
                      </TableCell>
                      <TableCell>
                        {formatDate(subscriber.subscribed_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant={subscriber.active ? "outline" : "default"}
                            size="sm"
                            onClick={() => handleToggleStatus(subscriber.id, subscriber.active)}
                            title={subscriber.active ? "Deactivate subscriber" : "Activate subscriber"}
                          >
                            <Power className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(subscriber.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Preloader>

            {subscribers_pagination && (
              <DataTablePagination 
                pagination={subscribers_pagination} 
                onPageChange={handlePageChange}
              />
            )}

            {subscribers.length === 0 && (
              <div className="text-center py-8">
                <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No subscribers found.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}

NewsletterSubscribersIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>

export default NewsletterSubscribersIndex 